import { useState, useEffect } from 'react';
import { Accordion } from '../../../common/SharedComponents';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { useSelector } from 'react-redux';
import { BfiDataGrid, Column, MasterDetail } from '../../../common/SharedComponents';
import { GridSettingType } from '@apply/constants';
import { ApplActionsButton } from '../../common/ApplicationComponents';
import { getLoanTypeShortName, onRowPrepared } from '../../../loans/common/LoanFunctions';
import { Tooltip } from 'devextreme-react/tooltip';
import { borrowerTooltipText, addressTooltipText } from '../../../common/SharedFunctions';
import ReceivedPortfolioTable from './ReceivedPortfolioTable';
import { onCellDblClickAppraisalReview } from '../../common/ApplicationFunctions';

const OrderedTable = ({ gridStore }) => {
    const searchState = useSelector((state) => state.searchState);
    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const styles = {
        due: { whiteSpace: 'nowrap' },
        overdue: { color: 'red', whiteSpace: 'nowrap' },
    };

    const dataFunction = (criteria) => applicationApi.getGrid_Appraise_Received(criteria);

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.appraise);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const actionCellRender = (e) => {
        return (
            <ApplActionsButton
                data={e.data}
                parentElementId='received'
            />
        );
    };

    const onLoanTypeCellRender = (e) => {
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    const onHighlightCellRender = (e) => {
        if (!e.value) {
            return null;
        }
        return (
            <div>
                <span
                    className='reddot'
                    style={{ position: 'unset' }}
                ></span>
            </div>
        );
    };

    const onReceivedCellRender = (e) => {
        const receivedDate = new Date(e.data.receivedDate).toLocaleDateString('en-us');
        return (
            <>
                <span>{receivedDate}</span>
                <span>
                    {e.data.daysSinceReceived > 0 && (
                        <div>
                            <span style={e.data.daysSinceReceived >= 3 ? styles.overdue : styles.due}>
                                ({e.data.daysSinceReceived} ago)
                            </span>
                        </div>
                    )}
                </span>
            </>
        );
    };

    const onBorrowerCellRender = (e) => {
        const targetId = `be_${e.data.id}`;
        const text = borrowerTooltipText(e.data.borrower);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.borrowerEntity}
                    {e.data.propertyCount > 1 && (
                        <span>
                            <b>&nbsp;[+{e.data.propertyCount}]</b>
                        </span>
                    )}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {text}
                </Tooltip>
            </>
        );
    };

    const onCityCellRender = (e) => {
        const targetId = `ac_${e.data.id}`;
        const text = addressTooltipText(e.data.address);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyCity}
                </div>
                {!e.data.isPortfolio && (
                    <Tooltip
                        target={`[data-for="${targetId}"]`}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const onStateCellRender = (e) => {
        const targetId = `as_${e.data.id}}`;
        const text = addressTooltipText(e.data.address);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyState}
                </div>
                {!e.data.isPortfolio && (
                    <Tooltip
                        target={`[data-for="${targetId}"]`}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const renderPortfolioRecords = (e) => {
        if (!e.data.isPortfolio) {
            return '';
        }
        return (
            <div style={{ padding: '10px 10px 10px 10px' }}>
                <ReceivedPortfolioTable data={e.data.portfolio} />
            </div>
        );
    };

    return (
        <Accordion
            title='Received'
            subTitle='Indicate as soon as review complete'
        >
            <div className='card'>
                <div className='card-section'>
                    <BfiDataGrid
                        gridType={GridSettingType.ReceivedTable}
                        dataStore={dataStore}
                        applicationInsights={applicationInsights}
                        onRowPrepared={onRowPrepared}
                        onCellDblClick={onCellDblClickAppraisalReview}
                    >
                        <Column
                            dataField='highlight'
                            caption=''
                            width={20}
                            cellRender={onHighlightCellRender}
                            alignment='center'
                            allowReordering={false}
                            allowSorting={false}
                            allowResizing={false}
                        />
                        <Column
                            dataField='borrowerEntity'
                            caption='Entity'
                            minWidth={120}
                            cellRender={onBorrowerCellRender}
                            alignment='left'
                        />
                        <Column
                            dataField='propertyCity'
                            caption='City'
                            width={100}
                            alignment='left'
                            cellRender={onCityCellRender}
                        />
                        <Column
                            dataField='propertyState'
                            caption='State'
                            width={60}
                            alignment='left'
                            cellRender={onStateCellRender}
                        />
                        <Column
                            dataField='bfiNumber'
                            caption='BFI #'
                            dataType='string'
                            width={120}
                        />
                        <Column
                            dataField='createdDate'
                            caption='Applied'
                            dataType='date'
                            width={90}
                        />
                        <Column
                            dataField={'loanTypeId'}
                            caption='Type'
                            width={90}
                            cellRender={onLoanTypeCellRender}
                            alignment='left'
                        />
                        <Column
                            dataField='orderedDate'
                            caption='Ordered'
                            dataType='date'
                            width={100}
                        />
                        <Column
                            dataField='receivedDate'
                            caption='Received'
                            width={100}
                            cellRender={onReceivedCellRender}
                        />
                        <Column
                            dataField='cost'
                            caption='Cost'
                            width={90}
                            dataType='number'
                            format='currency'
                            alignment='left'
                        />
                        <Column
                            dataField='appraiserCompany'
                            caption='Appraiser'
                            dataType='string'
                            width={120}
                        />
                        <Column
                            dataField='notes'
                            caption='Notes'
                            dataType='string'
                            width={120}
                        />
                        <Column
                            alignment='center'
                            cellRender={actionCellRender}
                            width={50}
                            allowReordering={false}
                            allowSorting={false}
                            allowResizing={false}
                            type='actions'
                            fixed={true}
                            fixedPosition={'right'}
                        />
                        <MasterDetail
                            enabled={true}
                            render={renderPortfolioRecords}
                        />
                    </BfiDataGrid>
                </div>
            </div>
        </Accordion>
    );
};

export default OrderedTable;
